<template>
  <div>
  <div
    v-if="!aiChatPanelOpen"
    ref="floatingRoot"
    class="whatsapp-icon-container-with ai-chat-trigger-container"
    :style="floatingStyle"
  >
    <button class="ai-chat-open-btn" @click="aiChatOpen">
      {{ t('aiChatOpenButton') }}
      <span v-if="docsStatus.processing" class="ai-chat-badge">{{ docsStatus.processing }}</span>
      <span v-else-if="docsStatus.ready" class="ai-chat-badge">{{ docsStatus.ready }}</span>
    </button>
  </div>

  <!-- v-show, not v-if: the documents block in the thread keeps reading files,
       looking up places and feeding the button's badge while the panel is
       closed; unmounting it on close would stop all three. -->
  <transition name="ai-panel-slide">
    <div
      v-show="aiChatPanelOpen"
      class="ai-panel"
      :class="{ 'ai-panel--rtl': isHebrew }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >

      <div v-if="dragDepth > 0" class="ai-panel__drop-overlay">
        <span>{{ t('aiChatDropOverlay') }}</span>
      </div>

      <div class="ai-panel__header">
        <span class="ai-panel__title">{{ t('aiChatTitle') }}</span>
        <button class="ai-panel__close" @click="aiChatClose">✕</button>
      </div>

      <div class="ai-panel__context-bar" v-if="allRide && allRide.ride">
        <span>{{ allRide.ride.title }}</span>
        <span v-if="allRide.ride.start_date && allRide.ride.end_date">
          &nbsp;·&nbsp;{{ formatDate(allRide.ride.start_date) }} – {{ formatDate(allRide.ride.end_date) }}
        </span>
      </div>

      <!-- Shown even when the profile is empty. The strip is the only way into
           the drawer, and an empty profile is every existing ride: hidden, the
           manager's only route to the editor is /ride_information, which is a
           different route and calls aiChatReset() on the way out. -->
      <div class="ai-panel__profile-strip" v-if="profileIsCurrent">
        <span v-if="profileSummary" class="ai-panel__profile-summary">{{ profileSummary }}</span>
        <button
          v-else
          class="ai-panel__profile-summary ai-panel__profile-summary--empty"
          @click="openProfileDrawer"
        >{{ t('aiChatProfileStripEmpty') }}</button>
        <button class="ai-panel__profile-edit" :title="t('aiChatProfileDrawerTitle')" @click="openProfileDrawer">&#9998;</button>
      </div>

      <div class="ai-panel__messages" ref="messageContainer">

        <div v-if="!aiChatMessages.length" class="ai-panel__starters">
          <button type="button" class="ai-panel__drop-tile" @click="openFilePicker">
            <strong>{{ t('aiChatDropZoneTitle') }}</strong>
            <span>{{ t('aiChatDropZoneHint') }}</span>
            <span class="ai-panel__drop-tile-types" dir="ltr">JPG · PNG · PDF</span>
          </button>
          <button
            v-for="(q, i) in starterQuestions"
            :key="i"
            class="ai-panel__starter-btn"
            @click="sendQuickReply(q)"
          >
            {{ q }}
          </button>
        </div>

        <!-- The wrapping template keeps msgIndex equal to the index in
             aiChatMessages, which every card key below is built from, while
             an auto-continue turn (msg.synthetic) stays out of the transcript:
             the manager did not type it, so it is not shown as if they had. -->
        <template v-for="(msg, msgIndex) in aiChatMessages">
        <div
          v-if="!msg.synthetic"
          :key="msgIndex"
          :class="['ai-panel__message', msg.role === 'user' ? 'ai-panel__message--user' : 'ai-panel__message--assistant', { 'ai-panel__message--busy': acceptingAll[msgIndex] }]"
        >
          <!-- The bubble for files dropped on the chat: what was handed over,
               not text. -->
          <div v-if="msg.kind === 'documents'" class="ai-panel__doc-chips">
            <span v-for="(name, fIndex) in msg.files" :key="fIndex" class="ai-panel__doc-chip" dir="auto">{{ name }}</span>
          </div>
          <p v-else class="ai-panel__message-text">{{ msg.content }}</p>

          <template v-if="msg.role === 'assistant'">
            <div v-if="acceptAllPlan(msg, msgIndex).steps.length > 1" class="ai-panel__apply-all ai-panel__accept-all">
              <span class="ai-panel__apply-all-count">
                <template v-if="resolvingAnyCardPlace(msgIndex)">{{ t('aiChatResolvingPlaces') }}</template>
                <template v-else>
                  {{ acceptAllPlan(msg, msgIndex).steps.length }} {{ t('aiChatChangesReady') }}
                  <template v-if="acceptAllPlan(msg, msgIndex).skipped.length">
                    · {{ acceptAllPlan(msg, msgIndex).skipped.length }} {{ t('aiChatLeftForYou') }}
                  </template>
                </template>
              </span>
              <button
                class="ai-panel__add-btn ai-panel__accept-all-btn"
                :disabled="!!acceptingAll[msgIndex] || resolvingAnyCardPlace(msgIndex)"
                @click="acceptAll(msg, msgIndex)"
              >
                <span v-if="acceptingAll[msgIndex]">{{ t('aiChatApplyingAll') }}</span>
                <span v-else>✓ {{ t('aiChatAcceptAll') }}</span>
              </button>
            </div>
            <p v-if="acceptAllNotApplied[msgIndex]" class="ai-panel__manual-hint">
              {{ acceptAllNotApplied[msgIndex] }} {{ t('aiChatAcceptAllNotApplied') }}
            </p>
          </template>

          <div v-if="msg.suggestions && msg.suggestions.length" class="ai-panel__suggestions">
            <div
              v-for="(s, sIndex) in msg.suggestions"
              :key="sIndex"
              class="ai-panel__suggestion-card"
            >
              <div class="ai-panel__area-card-inner">
                <div class="ai-panel__suggestion-info">
                  <strong>{{ s.title }}</strong>
                  <span class="ai-panel__suggestion-day">{{ t('aiChatDay') }} {{ s.day }}</span>
                  <p class="ai-panel__suggestion-desc">{{ s.description }}</p>
                </div>

                <template v-if="!addedSet['s-' + msgIndex + '-' + sIndex]">
                  <span v-if="selectedPlaces['s-' + msgIndex + '-' + sIndex]" class="ai-panel__resolved-place">
                    📍 {{ selectedPlaces['s-' + msgIndex + '-' + sIndex].name }}
                  </span>
                  <span v-if="needsManualPlace['s-' + msgIndex + '-' + sIndex] && !selectedPlaces['s-' + msgIndex + '-' + sIndex]" class="ai-panel__manual-hint">
                    {{ t('aiChatManualPlace') }}
                  </span>
                  <gmap-autocomplete
                    :ref="'placeInput-s-' + msgIndex + '-' + sIndex"
                    :class="['ai-panel__area-autocomplete', { 'ai-panel__area-autocomplete--manual': needsManualPlace['s-' + msgIndex + '-' + sIndex] && !selectedPlaces['s-' + msgIndex + '-' + sIndex] }]"
                    :placeholder="selectedPlaces['s-' + msgIndex + '-' + sIndex] ? t('aiChatChangePlace') : s.title"
                    :options="{ fields: ['place_id', 'name', 'geometry', 'formatted_address', 'address_components'] }"
                    :select-first-on-enter="true"
                    @place_changed="place => onSuggestionPlaceChanged(place, msgIndex, sIndex)"
                  />
                  <button
                    class="ai-panel__add-btn"
                    :disabled="!selectedPlaces['s-' + msgIndex + '-' + sIndex]"
                    @click="addSuggestion(s, msgIndex, sIndex)"
                  >
                    + {{ t('aiChatAdd') }}
                  </button>
                </template>
                <span v-else class="ai-panel__area-approved">✓ {{ t('aiChatAdded') }}</span>
              </div>
            </div>
          </div>

          <div v-if="msg.areas && msg.areas.length" class="ai-panel__suggestions">
            <div
              v-for="(area, aIndex) in msg.areas"
              :key="'a-' + aIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--area"
            >
              <div class="ai-panel__area-card-inner">
                <div class="ai-panel__suggestion-info">
                  <strong>{{ area.name }}</strong>
                  <span v-if="area.check_in || area.check_out" class="ai-panel__suggestion-day">
                    <span v-if="area.check_in">{{ t('aiChatAreaCheckIn') }}: {{ area.check_in }}</span>
                    <span v-if="area.check_in && area.check_out"> – </span>
                    <span v-if="area.check_out">{{ t('aiChatAreaCheckOut') }}: {{ area.check_out }}</span>
                  </span>
                </div>

                <template v-if="!addedSet['a-' + msgIndex + '-' + aIndex]">
                  <span v-if="selectedPlaces['a-' + msgIndex + '-' + aIndex]" class="ai-panel__resolved-place">
                    📍 {{ selectedPlaces['a-' + msgIndex + '-' + aIndex].name }}
                  </span>
                  <span v-if="needsManualPlace['a-' + msgIndex + '-' + aIndex] && !selectedPlaces['a-' + msgIndex + '-' + aIndex]" class="ai-panel__manual-hint">
                    {{ t('aiChatManualPlace') }}
                  </span>
                  <gmap-autocomplete
                    :ref="'placeInput-a-' + msgIndex + '-' + aIndex"
                    :class="['ai-panel__area-autocomplete', { 'ai-panel__area-autocomplete--manual': needsManualPlace['a-' + msgIndex + '-' + aIndex] && !selectedPlaces['a-' + msgIndex + '-' + aIndex] }]"
                    :placeholder="selectedPlaces['a-' + msgIndex + '-' + aIndex] ? t('aiChatAreaChange') : area.name"
                    :options="{ types: ['(cities)'], fields: ['place_id', 'name', 'geometry', 'formatted_address', 'address_components'] }"
                    :select-first-on-enter="true"
                    @place_changed="place => onAreaPlaceChanged(place, msgIndex, aIndex)"
                  />
                  <button
                    class="ai-panel__add-btn ai-panel__approve-btn"
                    :disabled="!selectedPlaces['a-' + msgIndex + '-' + aIndex]"
                    @click="approveArea(area, msgIndex, aIndex)"
                  >
                    {{ t('aiChatApproveArea') }}
                  </button>
                </template>
                <span v-else class="ai-panel__area-approved">✓ {{ t('aiChatAreaApproved') }}</span>
              </div>
            </div>
          </div>

          <div v-if="msg.hotel_suggestions && msg.hotel_suggestions.length" class="ai-panel__suggestions">
            <div
              v-for="(hotel, hIndex) in msg.hotel_suggestions"
              :key="'h-' + hIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--hotel"
            >
              <div class="ai-panel__area-card-inner">
                <div class="ai-panel__suggestion-info">
                  <strong>{{ hotel.name }}</strong>
                  <span v-if="hotel.check_in || hotel.check_out" class="ai-panel__suggestion-day">
                    <span v-if="hotel.check_in">{{ t('aiChatAreaCheckIn') }}: {{ hotel.check_in }}</span>
                    <span v-if="hotel.check_in && hotel.check_out"> – </span>
                    <span v-if="hotel.check_out">{{ t('aiChatAreaCheckOut') }}: {{ hotel.check_out }}</span>
                  </span>
                  <p v-if="hotel.description" class="ai-panel__suggestion-desc">{{ hotel.description }}</p>
                </div>

                <template v-if="!addedSet['h-' + msgIndex + '-' + hIndex]">
                  <span v-if="selectedPlaces['h-' + msgIndex + '-' + hIndex]" class="ai-panel__resolved-place">
                    📍 {{ selectedPlaces['h-' + msgIndex + '-' + hIndex].name }}
                  </span>
                  <span v-if="needsManualPlace['h-' + msgIndex + '-' + hIndex] && !selectedPlaces['h-' + msgIndex + '-' + hIndex]" class="ai-panel__manual-hint">
                    {{ t('aiChatManualPlace') }}
                  </span>
                  <gmap-autocomplete
                    :ref="'placeInput-h-' + msgIndex + '-' + hIndex"
                    :class="['ai-panel__area-autocomplete', { 'ai-panel__area-autocomplete--manual': needsManualPlace['h-' + msgIndex + '-' + hIndex] && !selectedPlaces['h-' + msgIndex + '-' + hIndex] }]"
                    :placeholder="selectedPlaces['h-' + msgIndex + '-' + hIndex] ? t('aiChatAreaChange') : hotel.name"
                    :options="{ types: ['establishment'], fields: ['place_id', 'name', 'geometry', 'formatted_address', 'address_components'] }"
                    :select-first-on-enter="true"
                    @place_changed="place => onHotelPlaceChanged(place, msgIndex, hIndex)"
                  />
                  <button
                    class="ai-panel__add-btn ai-panel__hotel-btn"
                    :disabled="!selectedPlaces['h-' + msgIndex + '-' + hIndex]"
                    @click="approveHotel(hotel, msgIndex, hIndex)"
                  >
                    {{ t('aiChatAddHotel') }}
                  </button>
                </template>
                <span v-else class="ai-panel__hotel-added">✓ {{ t('aiChatHotelAdded') }}</span>
              </div>
            </div>
          </div>

          <div v-if="msg.area_updates && msg.area_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(au, auIndex) in msg.area_updates"
              :key="'u-' + auIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--area-update"
            >
              <div class="ai-panel__area-card-inner">
                <div class="ai-panel__suggestion-info">
                  <strong>{{ au.name }}</strong>
                  <span v-if="au.check_in || au.check_out" class="ai-panel__suggestion-day">
                    <span v-if="au.check_in">{{ t('aiChatAreaCheckIn') }}: {{ au.check_in }}</span>
                    <span v-if="au.check_in && au.check_out"> – </span>
                    <span v-if="au.check_out">{{ t('aiChatAreaCheckOut') }}: {{ au.check_out }}</span>
                  </span>
                </div>

                <template v-if="!addedSet['u-' + msgIndex + '-' + auIndex]">
                  <span v-if="selectedPlaces['u-' + msgIndex + '-' + auIndex]" class="ai-panel__resolved-place">
                    📍 {{ selectedPlaces['u-' + msgIndex + '-' + auIndex].name }}
                  </span>
                  <span v-if="needsManualPlace['u-' + msgIndex + '-' + auIndex] && !selectedPlaces['u-' + msgIndex + '-' + auIndex]" class="ai-panel__manual-hint">
                    {{ t('aiChatManualPlace') }}
                  </span>
                  <gmap-autocomplete
                    :ref="'placeInput-u-' + msgIndex + '-' + auIndex"
                    :class="['ai-panel__area-autocomplete', { 'ai-panel__area-autocomplete--manual': needsManualPlace['u-' + msgIndex + '-' + auIndex] && !selectedPlaces['u-' + msgIndex + '-' + auIndex] }]"
                    :placeholder="selectedPlaces['u-' + msgIndex + '-' + auIndex] ? t('aiChatAreaChange') : au.name"
                    :options="{ types: ['(cities)'], fields: ['place_id', 'name', 'geometry', 'formatted_address', 'address_components'] }"
                    :select-first-on-enter="true"
                    @place_changed="place => onUpdatePlaceChanged(place, msgIndex, auIndex)"
                  />
                  <button
                    class="ai-panel__add-btn ai-panel__update-btn"
                    :disabled="!selectedPlaces['u-' + msgIndex + '-' + auIndex]"
                    @click="updateArea(au, msgIndex, auIndex)"
                  >
                    {{ t('aiChatUpdateArea') }}
                  </button>
                </template>
                <span v-else class="ai-panel__area-updated">✓ {{ t('aiChatAreaUpdated') }}</span>
              </div>
            </div>
          </div>

          <div v-if="msg.reschedules && msg.reschedules.length" class="ai-panel__suggestions">
            <div
              v-for="(r, rIndex) in msg.reschedules"
              :key="'r-' + rIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--reschedule"
            >
              <div class="ai-panel__suggestion-info">
                <strong>{{ r.attraction_title }}</strong>
                <span class="ai-panel__suggestion-day">
                  {{ t('aiChatMoveTo') }} {{ t('aiChatDay') }} {{ r.new_day }}
                  <span v-if="r.new_time && r.new_time !== 'no specific time'"> · {{ r.new_time }}</span>
                </span>
                <p class="ai-panel__suggestion-desc">{{ r.reason }}</p>
              </div>
              <button
                class="ai-panel__add-btn ai-panel__move-btn"
                :disabled="!!addedSet['r-' + msgIndex + '-' + rIndex]"
                @click="applyReschedule(r, msgIndex, rIndex)"
              >
                <span v-if="addedSet['r-' + msgIndex + '-' + rIndex]">✓ {{ t('aiChatMoved') }}</span>
                <span v-else>{{ t('aiChatMove') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.removals && msg.removals.length" class="ai-panel__suggestions">
            <div
              v-for="(rm, rmIndex) in msg.removals"
              :key="'x-' + rmIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--removal"
            >
              <div class="ai-panel__suggestion-info">
                <strong>{{ rm.attraction_title }}</strong>
                <p class="ai-panel__suggestion-desc">{{ rm.reason }}</p>
              </div>
              <button
                class="ai-panel__add-btn ai-panel__remove-btn"
                :disabled="!!addedSet['x-' + msgIndex + '-' + rmIndex]"
                @click="applyRemoval(rm, msgIndex, rmIndex)"
              >
                <span v-if="addedSet['x-' + msgIndex + '-' + rmIndex]">✓ {{ t('aiChatRemoved') }}</span>
                <span v-else>{{ t('aiChatRemove') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.accommodation_removals && msg.accommodation_removals.length" class="ai-panel__suggestions">
            <div
              v-for="(ar, arIndex) in msg.accommodation_removals"
              :key="'ar-' + arIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--removal"
            >
              <div class="ai-panel__suggestion-info">
                <strong>🏨 {{ ar.accommodation_name }}</strong>
                <span v-if="isRealBooking(ar)" class="ai-panel__manual-hint">
                  {{ t('aiChatRemoveAccommodationWarning') }}
                </span>
                <p class="ai-panel__suggestion-desc">{{ ar.reason }}</p>
              </div>
              <button
                class="ai-panel__add-btn ai-panel__remove-btn"
                :disabled="!!addedSet['ar-' + msgIndex + '-' + arIndex]"
                @click="removeAccommodation(ar, msgIndex, arIndex)"
              >
                <span v-if="addedSet['ar-' + msgIndex + '-' + arIndex]">✓ {{ t('aiChatRemoved') }}</span>
                <span v-else>{{ t('aiChatRemoveAccommodation') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.accommodation_updates && msg.accommodation_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(hu, huIndex) in msg.accommodation_updates"
              :key="'hu-' + huIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--hotel"
            >
              <div class="ai-panel__suggestion-info">
                <strong>🏨 {{ hu.accommodation_name }}</strong>
                <span v-if="hu.new_name && hu.new_name !== hu.accommodation_name" class="ai-panel__suggestion-day">
                  {{ t('aiChatNewName') }}: {{ hu.new_name }}
                </span>
                <span v-if="hu.check_in || hu.check_out" class="ai-panel__suggestion-day">
                  <span v-if="hu.check_in">{{ t('aiChatAreaCheckIn') }}: {{ hu.check_in }}</span>
                  <span v-if="hu.check_in && hu.check_out"> – </span>
                  <span v-if="hu.check_out">{{ t('aiChatAreaCheckOut') }}: {{ hu.check_out }}</span>
                </span>
                <p v-if="hu.new_text" class="ai-panel__suggestion-desc">{{ hu.new_text }}</p>
                <p v-if="hu.append_text" class="ai-panel__suggestion-desc">+ {{ hu.append_text }}</p>
                <span class="ai-panel__manual-hint">{{ hu.reason }}</span>
                <span v-if="hu.check_in || hu.check_out" class="ai-panel__manual-hint">{{ t('aiChatHotelReservationNote') }}</span>
              </div>
              <button
                class="ai-panel__add-btn ai-panel__hotel-btn"
                :disabled="!!addedSet['hu-' + msgIndex + '-' + huIndex]"
                @click="applyAccommodationUpdate(hu, msgIndex, huIndex)"
              >
                <span v-if="addedSet['hu-' + msgIndex + '-' + huIndex]">✓ {{ t('aiChatHotelUpdated') }}</span>
                <span v-else>{{ t('aiChatUpdateHotel') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.ride_date_updates && msg.ride_date_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(rd, rdIndex) in msg.ride_date_updates"
              :key="'rd-' + rdIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--ride-date"
            >
              <div class="ai-panel__suggestion-info">
                <strong>📅 {{ t('aiChatRideDatesTitle') }}</strong>
                <span class="ai-panel__suggestion-day">
                  {{ t('aiChatRideNewStart') }}: {{ rd.new_start_date }}
                </span>
                <p class="ai-panel__suggestion-desc">{{ rd.reason }}</p>
                <span class="ai-panel__manual-hint">{{ t('aiChatRideDatesShiftNote') }}</span>
              </div>
              <button
                class="ai-panel__add-btn ai-panel__ride-date-btn"
                :disabled="!!addedSet['rd-' + msgIndex + '-' + rdIndex]"
                @click="applyRideDateUpdate(rd, msgIndex, rdIndex)"
              >
                <span v-if="addedSet['rd-' + msgIndex + '-' + rdIndex]">✓ {{ t('aiChatRideDatesUpdated') }}</span>
                <span v-else>{{ t('aiChatRideDatesUpdate') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.day_updates && msg.day_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(du, duIndex) in msg.day_updates"
              :key="'du-' + duIndex"
              class="ai-panel__suggestion-card ai-panel__suggestion-card--ride-date"
            >
              <div class="ai-panel__suggestion-info">
                <strong>
                  {{ du.operation === 'remove' ? '🗓️' : '➕' }}
                  {{ du.operation === 'remove' ? t('aiChatRemoveDayTitle') : t('aiChatAddDayTitle') }}
                </strong>
                <span class="ai-panel__suggestion-day">{{ t('aiChatDay') }} {{ du.day }}</span>
                <p class="ai-panel__suggestion-desc">{{ du.reason }}</p>
              </div>
              <button
                class="ai-panel__add-btn"
                :class="du.operation === 'remove' ? 'ai-panel__remove-btn' : 'ai-panel__ride-date-btn'"
                :disabled="!!addedSet['du-' + msgIndex + '-' + duIndex]"
                @click="applyDayUpdate(du, msgIndex, duIndex)"
              >
                <span v-if="addedSet['du-' + msgIndex + '-' + duIndex]">✓ {{ t('aiChatRideDatesUpdated') }}</span>
                <span v-else>{{ du.operation === 'remove' ? t('aiChatRemoveDay') : t('aiChatAddDay') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.attraction_updates && msg.attraction_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(au, auIndex) in msg.attraction_updates"
              :key="'ae-' + auIndex"
              class="ai-panel__suggestion-card"
            >
              <div class="ai-panel__suggestion-info">
                <strong>✏️ {{ au.attraction_title }}</strong>
                <span v-if="au.new_title && au.new_title !== au.attraction_title" class="ai-panel__suggestion-day">
                  {{ t('aiChatNewName') }}: {{ au.new_title }}
                </span>
                <p v-if="au.new_text" class="ai-panel__suggestion-desc">{{ au.new_text }}</p>
                <p v-if="au.append_text" class="ai-panel__suggestion-desc">+ {{ au.append_text }}</p>
                <span class="ai-panel__manual-hint">{{ au.reason }}</span>
              </div>
              <button
                class="ai-panel__add-btn"
                :disabled="!!addedSet['ae-' + msgIndex + '-' + auIndex]"
                @click="applyAttractionUpdate(au, msgIndex, auIndex)"
              >
                <span v-if="addedSet['ae-' + msgIndex + '-' + auIndex]">✓ {{ t('aiChatAttractionUpdated') }}</span>
                <span v-else>{{ t('aiChatUpdateAttraction') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.day_note_updates && msg.day_note_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(dn, dnIndex) in msg.day_note_updates"
              :key="'dn-' + dnIndex"
              class="ai-panel__suggestion-card"
              :class="{ 'ai-panel__suggestion-card--removal': dn.operation === 'remove' }"
            >
              <div class="ai-panel__suggestion-info">
                <strong>📝 {{ t(dayNoteTitleKey(dn)) }}</strong>
                <span class="ai-panel__suggestion-day">{{ t('aiChatDay') }} {{ dn.day }}</span>
                <p v-if="dn.title && dn.operation !== 'remove'" class="ai-panel__suggestion-desc"><b>{{ dn.title }}</b></p>
                <p v-if="dn.text && dn.operation !== 'remove'" class="ai-panel__suggestion-desc">{{ dn.text }}</p>
                <p v-if="dn.append_text && dn.operation !== 'remove'" class="ai-panel__suggestion-desc">+ {{ dn.append_text }}</p>
                <span class="ai-panel__manual-hint">{{ dn.reason }}</span>
              </div>
              <button
                class="ai-panel__add-btn"
                :class="{ 'ai-panel__remove-btn': dn.operation === 'remove' }"
                :disabled="!!addedSet['dn-' + msgIndex + '-' + dnIndex]"
                @click="applyDayNoteUpdate(dn, msgIndex, dnIndex)"
              >
                <span v-if="addedSet['dn-' + msgIndex + '-' + dnIndex]">
                  ✓ {{ dn.operation === 'remove' ? t('aiChatRemoved') : t('aiChatNoteSaved') }}
                </span>
                <span v-else>{{ dn.operation === 'remove' ? t('aiChatRemoveNote') : t('aiChatSaveNote') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.ride_note_updates && msg.ride_note_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(rn, rnIndex) in msg.ride_note_updates"
              :key="'rn-' + rnIndex"
              class="ai-panel__suggestion-card"
              :class="{ 'ai-panel__suggestion-card--removal': rn.operation === 'remove' }"
            >
              <div class="ai-panel__suggestion-info">
                <strong>📋 {{ t(rideNoteTitleKey(rn)) }}</strong>
                <span class="ai-panel__suggestion-day">{{ t('aiChatRideNoteSubtitle') }}</span>
                <p v-if="rn.title && rn.operation !== 'remove'" class="ai-panel__suggestion-desc"><b>{{ rn.title }}</b></p>
                <p v-if="rn.text && rn.operation !== 'remove'" class="ai-panel__suggestion-desc">{{ rn.text }}</p>
                <p v-if="rn.append_text && rn.operation !== 'remove'" class="ai-panel__suggestion-desc">+ {{ rn.append_text }}</p>
                <span class="ai-panel__manual-hint">{{ rn.reason }}</span>
              </div>
              <button
                class="ai-panel__add-btn"
                :class="{ 'ai-panel__remove-btn': rn.operation === 'remove' }"
                :disabled="!!addedSet['rn-' + msgIndex + '-' + rnIndex]"
                @click="applyRideNoteUpdate(rn, msgIndex, rnIndex)"
              >
                <span v-if="addedSet['rn-' + msgIndex + '-' + rnIndex]">
                  ✓ {{ rn.operation === 'remove' ? t('aiChatRemoved') : t('aiChatNoteSaved') }}
                </span>
                <span v-else>{{ rn.operation === 'remove' ? t('aiChatRemoveNote') : t('aiChatSaveNote') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.ride_text_updates && msg.ride_text_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(rt, rtIndex) in msg.ride_text_updates"
              :key="'rt-' + rtIndex"
              class="ai-panel__suggestion-card"
            >
              <div class="ai-panel__suggestion-info">
                <strong>📜 {{ t('aiChatRideTextTitle') }}</strong>
                <span class="ai-panel__suggestion-day">{{ t(rideTextSubtitleKey(rt)) }}</span>
                <p v-if="rt.new_text && currentRideTextLength" class="ai-panel__ride-text-warning">
                  {{ t('aiChatRideTextReplacesWarning') }} ({{ currentRideTextLength }} {{ t('aiChatRideTextChars') }})
                </p>
                <p v-if="rt.new_text" class="ai-panel__suggestion-desc ai-panel__ride-text">{{
                  rideTextShown(rt.new_text, 'rt-' + msgIndex + '-' + rtIndex, rt)
                }}</p>
                <p v-if="rt.append_text" class="ai-panel__suggestion-desc ai-panel__ride-text">+ {{
                  rideTextShown(rt.append_text, 'rt-' + msgIndex + '-' + rtIndex, rt)
                }}</p>
                <button
                  v-if="rideTextIsLong(rt)"
                  type="button"
                  class="ai-panel__ride-text-toggle"
                  @click="toggleRideText(rt, 'rt-' + msgIndex + '-' + rtIndex)"
                >
                  {{ rideTextIsExpanded(rt, 'rt-' + msgIndex + '-' + rtIndex)
                    ? t('aiChatRideTextShowLess') : t('aiChatRideTextShowAll') }}
                </button>
                <span class="ai-panel__manual-hint">{{ rt.reason }}</span>
              </div>
              <button
                class="ai-panel__add-btn"
                :disabled="!!addedSet['rt-' + msgIndex + '-' + rtIndex]"
                @click="applyRideTextUpdate(rt, msgIndex, rtIndex)"
              >
                <span v-if="addedSet['rt-' + msgIndex + '-' + rtIndex]">✓ {{ t('aiChatRideTextUpdated') }}</span>
                <span v-else>{{ t('aiChatUpdateRideText') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.ride_title_updates && msg.ride_title_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(tt, ttIndex) in msg.ride_title_updates"
              :key="'tt-' + ttIndex"
              class="ai-panel__suggestion-card"
            >
              <div class="ai-panel__suggestion-info">
                <strong>🏷️ {{ t('aiChatRideTitleTitle') }}</strong>
                <span class="ai-panel__suggestion-day">{{ t('aiChatRideTitleReplaces') }}</span>
                <span v-if="currentRideTitle" class="ai-panel__profile-change-row">
                  <span class="ai-panel__profile-change-tag">{{ t('aiChatRideTitleCurrent') }}</span>
                  <span class="ai-panel__profile-change-value" dir="auto">{{ currentRideTitle }}</span>
                </span>
                <span class="ai-panel__profile-change-row">
                  <span class="ai-panel__profile-change-tag">{{ t('aiChatRideTitleNew') }}</span>
                  <span class="ai-panel__profile-change-value" dir="auto">{{ rideTitleShown(tt) }}</span>
                </span>
                <p v-if="rideTitleProblem(tt)" class="ai-panel__ride-text-warning">
                  {{ t(rideTitleProblemKey(tt), { max: MAX_RIDE_TITLE_LENGTH }) }}
                </p>
                <span class="ai-panel__manual-hint">{{ tt.reason }}</span>
              </div>
              <button
                class="ai-panel__add-btn"
                :disabled="!!addedSet['tt-' + msgIndex + '-' + ttIndex] || !!rideTitleProblem(tt)"
                @click="applyRideTitleUpdate(tt, msgIndex, ttIndex)"
              >
                <span v-if="addedSet['tt-' + msgIndex + '-' + ttIndex]">✓ {{ t('aiChatRideTitleUpdated') }}</span>
                <span v-else>{{ t('aiChatUpdateRideTitle') }}</span>
              </button>
            </div>
          </div>

          <div v-if="msg.ride_language_updates && msg.ride_language_updates.length" class="ai-panel__suggestions">
            <div
              v-for="(rl, rlIndex) in msg.ride_language_updates"
              :key="'rl-' + rlIndex"
              class="ai-panel__suggestion-card"
            >
              <div class="ai-panel__suggestion-info">
                <strong>🌐 {{ t('aiChatRideLanguageTitle') }}</strong>
                <span class="ai-panel__suggestion-day">{{ t('aiChatRideLanguageReplaces') }}</span>
                <span v-if="currentRideLanguage" class="ai-panel__profile-change-row">
                  <span class="ai-panel__profile-change-tag">{{ t('aiChatRideLanguageCurrent') }}</span>
                  <span class="ai-panel__profile-change-value" dir="auto">{{ rideLanguageName(currentRideLanguage) }}</span>
                </span>
                <span class="ai-panel__profile-change-row">
                  <span class="ai-panel__profile-change-tag">{{ t('aiChatRideLanguageNew') }}</span>
                  <span class="ai-panel__profile-change-value" dir="auto">{{ rideLanguageName(rideLanguageShown(rl)) }}</span>
                </span>
                <p v-if="rideLanguageProblem(rl)" class="ai-panel__ride-text-warning">
                  {{ t(rideLanguageProblem(rl) === 'unknown_language' ? 'aiChatRideLanguageUnknown' : 'aiChatRideLanguageEmpty') }}
                </p>
                <p v-else-if="rideLanguageReloads(rl)" class="ai-panel__ride-text-warning">
                  {{ t('aiChatRideLanguageReloadWarning') }}
                </p>
                <span class="ai-panel__manual-hint">{{ rl.reason }}</span>
              </div>
              <button
                class="ai-panel__add-btn"
                :disabled="!!addedSet['rl-' + msgIndex + '-' + rlIndex] || !!rideLanguageProblem(rl)"
                @click="applyRideLanguageUpdate(rl, msgIndex, rlIndex)"
              >
                <span v-if="addedSet['rl-' + msgIndex + '-' + rlIndex]">✓ {{ t('aiChatRideLanguageUpdated') }}</span>
                <span v-else>{{ t('aiChatUpdateRideLanguage') }}</span>
              </button>
            </div>
          </div>

          <div
            v-if="msg.role === 'assistant' && msg.quick_replies && msg.quick_replies.length && msgIndex === lastAssistantIndex && !aiChatLoading"
            class="ai-panel__quick-replies"
          >
            <button
              v-for="(qr, qrIndex) in msg.quick_replies"
              :key="qrIndex"
              class="ai-panel__quick-reply-btn"
              @click="sendQuickReply(qr)"
            >
              {{ qr }}
            </button>
          </div>
        </div>
        </template>

        <!-- Every document card the manager has staged, live, after the
             transcript. One block rather than one per upload: the cards are a
             single store list, and the ones still waiting on the manager stay
             next to the composer instead of scrolling away. -->
        <RideDocumentsPanel
          v-if="rideHeader && rideHeader.ride"
          ref="documents"
          :rideId="rideHeader.ride.id"
          @attached="aiChatNoteAttachedDocuments"
          @status="docsStatus = $event"
        />

        <div v-if="visibleProfileChanges.length" class="ai-panel__suggestions">
          <div
            v-for="change in visibleProfileChanges"
            :key="'pc-' + change.field"
            class="ai-panel__suggestion-card ai-panel__suggestion-card--profile"
          >
            <div class="ai-panel__suggestion-info">
              <strong>{{ t(profileFieldKey(change.field)) }}</strong>
              <span class="ai-panel__profile-change-row">
                <span class="ai-panel__profile-change-tag">{{ t('aiChatProfileSetByYou') }}</span>
                <span class="ai-panel__profile-change-value" dir="auto">{{ change.current }}</span>
              </span>
              <span class="ai-panel__profile-change-row">
                <span class="ai-panel__profile-change-tag">{{ t('aiChatProfileAiProposes') }}</span>
                <span class="ai-panel__profile-change-value" dir="auto">{{ change.proposed }}</span>
              </span>
              <span v-if="profileChangeError[change.field]" class="ai-panel__profile-change-error">{{ t('aiChatProfileSaveFailed') }}</span>
            </div>
            <div class="ai-panel__profile-change-actions">
              <button
                class="ai-panel__add-btn"
                :disabled="!!profileChangeBusy[change.field]"
                @click="acceptProfileChange(change)"
              >{{ t('aiChatProfileUseAi') }}</button>
              <button
                class="ai-panel__profile-keep-btn"
                :disabled="!!profileChangeBusy[change.field]"
                @click="aiChatDismissProfileChange({ field: change.field })"
              >{{ t('aiChatProfileKeepMine') }}</button>
            </div>
          </div>
        </div>

        <div v-if="aiChatLoading" class="ai-panel__typing">
          <span class="ai-panel__typing-dot"></span>
          <span class="ai-panel__typing-dot"></span>
          <span class="ai-panel__typing-dot"></span>
        </div>
      </div>

      <div class="ai-panel__input-area">
        <p v-if="fileNotice" class="ai-panel__file-notice" role="status">{{ fileNotice }}</p>
        <div class="ai-panel__composer-row">
          <button
            type="button"
            class="ai-panel__attach-btn"
            :aria-label="t('aiChatAttachFiles')"
            :title="t('aiChatAttachFiles')"
            @click="openFilePicker"
          ><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.5l-8.6 8.6a5 5 0 01-7.1-7.1l8.6-8.6a3.3 3.3 0 014.7 4.7l-8.6 8.6a1.7 1.7 0 01-2.4-2.4l7.9-7.9"></path></svg></button>
          <input
            ref="fileInput"
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.pdf"
            style="display:none"
            @change="onFileInput"
          />
          <textarea
            ref="inputArea"
            v-model="draft"
            class="ai-panel__textarea"
            :placeholder="t('aiChatPlaceholder')"
            :disabled="aiChatLoading"
            rows="3"
            @keydown.enter.exact.prevent="send"
          ></textarea>
        </div>
        <button
          class="ai-panel__send-btn"
          :disabled="!draft.trim() || aiChatLoading"
          @click="send"
        >
          {{ t('aiChatSend') }}
        </button>
      </div>


      <div v-if="profileDrawerOpen && profileIsCurrent" class="ai-panel__profile-drawer">
        <div class="ai-panel__profile-drawer-header">
          <span class="ai-panel__title">{{ t('aiChatProfileDrawerTitle') }}</span>
          <button class="ai-panel__close" @click="profileDrawerOpen = false">✕</button>
        </div>
        <p v-if="profileSaveError" class="ai-panel__profile-error">{{ t('aiChatProfileSaveFailed') }}</p>
        <TripProfileEditor
          embedded
          :profile="aiTripProfile"
          @save="saveProfile"
          @cancel="profileDrawerOpen = false"
        />
      </div>
    </div>
  </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';
import { resolvePlace } from '@/common/resolvePlace';
import TripProfileEditor from '@/components/TripProfileEditor.vue';
import RideDocumentsPanel from '@/components/RideDocumentsPanel.vue';
import { summaryLine, profileFieldLabelKey } from '@/utils/tripProfile';
import {
  resolveAccommodationKind, dayIndexForDayNumber, planAcceptAll, outstandingCards,
  normalizeRideTitle, MAX_RIDE_TITLE_LENGTH, normalizeRideLanguage, rideLanguageSwitchesInterface,
} from '@/utils/aiChatApply';
import { rideLanguageLabel } from '@/common/rideLanguageOptions';
import {
  getFloatingItemStyle,
  registerFloatingItem,
  unregisterFloatingItem,
  updateFloatingItem,
} from '@/services/floatingItems';

// How much of a proposed trip description the card shows before the manager
// opens it. The panel is a narrow column beside the itinerary, so a long
// description shown whole would push every other card out of sight.
const RIDE_TEXT_PREVIEW = 220;

export default {
  name: 'AiChatPanel',
  components: { TripProfileEditor, RideDocumentsPanel },
  mixins: [translationMixin],
  data() {
    return {
      // The database's own limit on Ride.title, shown in the card's refusal
      // copy rather than hard-coded in two languages of translation.
      MAX_RIDE_TITLE_LENGTH,
      draft: '',
      addedSet: {},
      acceptingAll: {},
      acceptAllNotApplied: {},
      // While "Accept all" runs, card errors are collected instead of popping
      // one modal per card; what failed keeps its own button.
      bulkMode: false,
      resolvingPlaces: {},
      selectedPlaces: {},
      needsManualPlace: {},
      // card key -> whether its full proposed description is shown. Absent
      // means "as the card opened": expanded for a replacement, previewed for
      // an append-only card (see rideTextStartsExpanded).
      expandedRideText: {},
      // msgIndex -> true once that reply has produced its one auto-continue
      // turn. Part of the loop guard: one reply can never cause two.
      autoContinuedFor: {},
      floatingId: 'floating-ai-chat',
      profileDrawerOpen: false,
      profileSaveError: false,
      // field -> true while its accept is in flight / after it failed
      profileChangeBusy: {},
      profileChangeError: {},
      // What the embedded documents block reports, for the closed button's badge.
      docsStatus: { processing: 0, ready: 0 },
      // Nested dragenter/dragleave pairs from child elements; the overlay is up
      // while this is above zero.
      dragDepth: 0,
      // Shown under the composer when files were refused or there was no room.
      fileNotice: '',
      // The draft looks like a pasted booking: ask before sending it to the model.
    };
  },
  computed: {
    ...mapGetters(['aiChatMessages', 'aiChatLoading', 'aiChatPanelOpen', 'allRide', 'aiTripProfile', 'aiTripProfilePending', 'rideId', 'rideHeader']),
    // The stored profile belongs to the ride it was seeded for; if the ride changed
    // under an open panel, show nothing rather than another ride's profile.
    profileIsCurrent() {
      return this.$store.state.aiChat.tripProfileRideId === this.rideId;
    },
    // Pending changes belong to the stored profile, so they obey the same ride gate.
    visibleProfileChanges() {
      return this.profileIsCurrent ? this.aiTripProfilePending : [];
    },
    profileSummary() {
      return this.profileIsCurrent ? summaryLine(this.aiTripProfile, this.t) : '';
    },
    floatingStyle() {
      return getFloatingItemStyle(this.floatingId);
    },
    // How much of the manager's own writing a replacement card would
    // overwrite. Read from rideHeader, which is where the ride page renders
    // the description from, and where this feature writes it back.
    currentRideTextLength() {
      const ride = this.rideHeader && this.rideHeader.ride;
      return ((ride && ride.text) || '').trim().length;
    },
    // The name the trip has now. Shown beside the proposed one, because a
    // rename overwrites it with no undo and the manager is approving the
    // swap, not the new name on its own. Read from rideHeader, which is both
    // where the ride page renders the title from and what this card refreshes.
    currentRideTitle() {
      const ride = this.rideHeader && this.rideHeader.ride;
      return ((ride && ride.title) || '').trim();
    },
    // The language the trip is in now, read from the same ride header the ride
    // page sets its interface language from.
    currentRideLanguage() {
      const ride = this.rideHeader && this.rideHeader.ride;
      return (ride && ride.language) || '';
    },
    starterQuestions() {
      return [
        this.t('aiChatStarter1'),
        this.t('aiChatStarter2'),
        this.t('aiChatStarter3'),
        this.t('aiChatStarter4'),
      ];
    },
    lastAssistantIndex() {
      for (let i = this.aiChatMessages.length - 1; i >= 0; i--) {
        if (this.aiChatMessages[i].role === 'assistant') return i;
      }
      return -1;
    },
  },
  watch: {
    aiChatMessages(messages) {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (!container) return;
        // Land on the TOP of the new reply, not the bottom of the panel. A reply
        // carrying a dozen cards is taller than the panel, and scrolling to the
        // bottom dropped the manager below the whole list -- past the reply text
        // and past the green "accept all" bar, with no sign either existed.
        // Not :last-of-type -- that matches by tag, and the documents block
        // after the transcript is a <div> too, so it would match nothing.
        const all = container.querySelectorAll('.ai-panel__message');
        const last = all[all.length - 1];
        container.scrollTop = last
          ? Math.max(0, last.offsetTop - container.offsetTop)
          : container.scrollHeight;
      });
      const lastMsg = messages[messages.length - 1];
      if (lastMsg) {
        const msgIndex = messages.length - 1;
        (lastMsg.suggestions || []).forEach((s, i) => this.autoResolvePlace(`s-${msgIndex}-${i}`, s.place_query, s.title));
        (lastMsg.areas || []).forEach((area, i) => this.autoResolvePlace(`a-${msgIndex}-${i}`, area.place_query, area.name));
        (lastMsg.area_updates || []).forEach((au, i) => this.autoResolvePlace(`u-${msgIndex}-${i}`, au.place_query, au.name));
        (lastMsg.hotel_suggestions || []).forEach((hotel, i) => this.autoResolvePlace(`h-${msgIndex}-${i}`, hotel.place_query, hotel.name));
      }
    },
    aiChatLoading(loading) {
      if (!loading) {
        this.$nextTick(() => {
          if (this.$refs.inputArea) this.$refs.inputArea.focus();
        });
      }
    },
    // A drawer left open across a ride change would show and save the old ride's values.
    // Everything keyed by message index goes too: SingleRideWithMap clears the
    // transcript on the same change (navigating ride A -> ride B reuses the
    // component, so beforeDestroy never runs), and indices restart from zero.
    // autoContinuedFor especially -- left behind, the new ride's first reply
    // would inherit ride A's "already continued" marks and never cross a gate.
    rideId() {
      this.profileDrawerOpen = false;
      this.profileSaveError = false;
      this.profileChangeBusy = {};
      this.profileChangeError = {};
      this.autoContinuedFor = {};
      this.addedSet = {};
      this.acceptingAll = {};
      this.acceptAllNotApplied = {};
      this.resolvingPlaces = {};
      this.selectedPlaces = {};
      this.needsManualPlace = {};
    },
    aiChatPanelOpen(open) {
      if (!open) this.profileDrawerOpen = false;
      this.syncFloatingState();
      this.syncBodyClass();
    },
  },
  mounted() {
    registerFloatingItem({
      id: this.floatingId,
      priority: 50,
      anchor: 'right',
      zIndex: 1002,
    });
    this.syncFloatingState();
    this.syncBodyClass();
  },
  beforeDestroy() {
    unregisterFloatingItem(this.floatingId);
    document.body.classList.remove('ai-panel-open');
  },
  methods: {
    ...mapActions(['aiChatClose', 'aiChatSend', 'aiChatAddSuggestion', 'aiChatReschedule', 'aiChatRemoveAttraction', 'aiChatRemoveAccommodation', 'aiChatUpdateRideStartDate', 'aiChatUpdateAttraction', 'aiChatUpdateAccommodation', 'aiChatApplyDayNote', 'aiChatApplyRideNote', 'aiChatUpdateRideText', 'aiChatUpdateRideTitle', 'aiChatUpdateRideLanguage', 'addNewDayToRide', 'removeDayFromRide', 'showModalWithParameters', 'aiChatOpen', 'aiChatApproveArea', 'aiChatUpdateArea', 'aiChatApproveHotel', 'aiChatSaveProfile', 'aiChatDismissProfileChange', 'aiChatAutoContinue', 'aiChatAddDocumentsMessage', 'aiChatNoteAttachedDocuments']),
    // Lets the global style below lift confirmation popups above the panel.
    syncBodyClass() {
      document.body.classList.toggle('ai-panel-open', !!this.aiChatPanelOpen);
    },
    openProfileDrawer() {
      this.profileSaveError = false;
      this.profileDrawerOpen = true;
    },
    async saveProfile(profile) {
      const result = await this.aiChatSaveProfile({ profile });
      // stale: the save landed on the previous ride and the store was not updated,
      // so no confirmation; the drawer just closes.
      if (result && result.success) {
        this.profileSaveError = false;
        this.profileDrawerOpen = false;
      } else {
        this.profileSaveError = true;
      }
    },
    profileFieldKey(field) {
      return profileFieldLabelKey(field);
    },
    async acceptProfileChange(change) {
      const field = change.field;
      if (this.profileChangeBusy[field]) return;
      this.$set(this.profileChangeBusy, field, true);
      this.$set(this.profileChangeError, field, false);
      try {
        const result = await this.aiChatSaveProfile({ profile: { [field]: change.proposed } });
        // Success drops the card via the store; a stale save left the store alone
        // (the ride changed), so there is nothing to confirm here either.
        if (!(result && result.success)) this.$set(this.profileChangeError, field, true);
      } finally {
        this.$set(this.profileChangeBusy, field, false);
      }
    },
    syncFloatingState() {
      this.$nextTick(() => {
        const root = this.$refs.floatingRoot;
        updateFloatingItem(this.floatingId, {
          visible: !this.aiChatPanelOpen,
          height: !this.aiChatPanelOpen && root ? root.offsetHeight : 0,
        });
      });
    },
    // Text always goes to the chat, a pasted confirmation included: the chat
    // can add the stay or activity itself. Only files go to the document reader.
    async send() {
      const msg = this.draft.trim();
      if (!msg || this.aiChatLoading) return;
      this.fileNotice = '';
      this.draft = '';
      if (this.$refs.documents) this.$refs.documents.dismissAttachSuccess();
      await this.aiChatSend(msg);
    },
    openFilePicker() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    onFileInput(e) {
      this.attachFiles(e.target.files);
      e.target.value = '';
    },
    // Only a drag that carries files raises the overlay; dragging text or a
    // link across the panel is left alone.
    onDragEnter(e) {
      const types = (e.dataTransfer && e.dataTransfer.types) || [];
      if (Array.prototype.indexOf.call(types, 'Files') < 0) return;
      this.dragDepth += 1;
    },
    onDragLeave() {
      if (this.dragDepth > 0) this.dragDepth -= 1;
    },
    onDrop(e) {
      const hadFiles = this.dragDepth > 0;
      this.dragDepth = 0;
      if (hadFiles && e.dataTransfer) this.attachFiles(e.dataTransfer.files);
    },
    // Hands files to the documents block, which uploads what it can; the chat
    // shows a bubble for what was taken and a line for what was not.
    attachFiles(fileList) {
      const documents = this.$refs.documents;
      if (!documents) return;
      const { accepted, rejected, overLimit } = documents.addFiles(Array.from(fileList || []));
      this.fileNotice = rejected.length || overLimit ? this.t('aiChatFilesRejected') : '';
      if (accepted.length) this.aiChatAddDocumentsMessage({ files: accepted });
    },
    async sendQuickReply(text) {
      if (this.aiChatLoading) return;
      await this.aiChatSend(text);
    },
    onSuggestionPlaceChanged(place, messageIndex, suggestionIndex) {
      const key = `s-${messageIndex}-${suggestionIndex}`;
      this.$set(this.selectedPlaces, key, place);
    },
    async addSuggestion(suggestion, messageIndex, suggestionIndex) {
      const key = `s-${messageIndex}-${suggestionIndex}`;
      if (this.addedSet[key]) return;
      const place = this.selectedPlaces[key];
      if (!place) return;
      await this.aiChatAddSuggestion({ suggestion, place });
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },
    onAreaPlaceChanged(place, messageIndex, areaIndex) {
      const key = `a-${messageIndex}-${areaIndex}`;
      this.$set(this.selectedPlaces, key, place);
    },
    onHotelPlaceChanged(place, messageIndex, hIndex) {
      const key = `h-${messageIndex}-${hIndex}`;
      this.$set(this.selectedPlaces, key, place);
    },
    async approveHotel(hotel, messageIndex, hIndex) {
      const key = `h-${messageIndex}-${hIndex}`;
      if (this.addedSet[key]) return;
      const place = this.selectedPlaces[key];
      if (!place) return;
      const payload = {
        name: place.name || hotel.name,
        cuntry_and_city: this.extractCityCountry(place),
        check_in: hotel.check_in,
        check_out: hotel.check_out,
        address: place.formatted_address || '',
        place_id: place.place_id || '',
        latitude: place.geometry ? place.geometry.location.lat() : null,
        longitude: place.geometry ? place.geometry.location.lng() : null,
        text: hotel.description || '',
      };
      const result = await this.aiChatApproveHotel(payload);
      // The accommodation endpoint rejects dates outside the trip and nights
      // already booked; ticking the card regardless would claim a hotel the
      // ride never got. Its codes already have popups in errorNumberConverter.
      if (!result || !result.success) {
        this.reportCardError((result && result.code) || 'fail_to_update_attraction');
        return;
      }
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },
    onUpdatePlaceChanged(place, messageIndex, auIndex) {
      const key = `u-${messageIndex}-${auIndex}`;
      this.$set(this.selectedPlaces, key, place);
    },
    async updateArea(areaUpdate, messageIndex, auIndex) {
      const key = `u-${messageIndex}-${auIndex}`;
      if (this.addedSet[key]) return;
      const place = this.selectedPlaces[key];
      if (!place) return;
      const payload = {
        name: place.name || areaUpdate.name,
        cuntry_and_city: this.extractCityCountry(place),
        check_in: areaUpdate.check_in,
        check_out: areaUpdate.check_out,
        address: place.formatted_address || '',
        place_id: place.place_id || '',
        latitude: place.geometry ? place.geometry.location.lat() : null,
        longitude: place.geometry ? place.geometry.location.lng() : null,
      };
      // update_suggested_area_api only accepts ai_suggested_area rows, so an
      // area carried over from a previous ride comes back 403 and the await
      // rejects -- which used to leave the button doing nothing at all.
      try {
        await this.aiChatUpdateArea({ accommodationId: areaUpdate.id, area: payload });
      } catch (e) {
        console.warn('[aiChat] updateArea failed', e);
        this.reportCardError('fail_to_update_attraction');
        return;
      }
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },
    async approveArea(area, messageIndex, areaIndex) {
      const key = `a-${messageIndex}-${areaIndex}`;
      if (this.addedSet[key]) return;
      const place = this.selectedPlaces[key];
      if (!place) return;
      const payload = {
        name: place.name || area.name,
        cuntry_and_city: this.extractCityCountry(place),
        check_in: area.check_in,
        check_out: area.check_out,
        address: place.formatted_address || '',
        place_id: place.place_id || '',
        latitude: place.geometry ? place.geometry.location.lat() : null,
        longitude: place.geometry ? place.geometry.location.lng() : null,
      };
      await this.aiChatApproveArea(payload);
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },
    async applyReschedule(reschedule, messageIndex, rescheduleIndex) {
      const key = `r-${messageIndex}-${rescheduleIndex}`;
      if (this.addedSet[key]) return;
      await this.aiChatReschedule({
        attractionId: reschedule.attraction_id,
        newDay: reschedule.new_day,
        newTime: reschedule.new_time,
      });
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },
    // --- Adding and removing days ---
    //
    // Both dispatch the ride store's own actions, which already handle the
    // booked-accommodation force modal, error popups and the refetch. Removal
    // uses the day-only endpoint: by the time a remove card appears the day has
    // been emptied in the conversation, so the delete-attractions endpoint --
    // which drops them before its own conflict check -- is never reached.
    async applyDayUpdate(update, messageIndex, duIndex) {
      const key = `du-${messageIndex}-${duIndex}`;
      if (this.addedSet[key]) return;

      const totalDays = this.allRide && this.allRide.attractionsByDay
        ? this.allRide.attractionsByDay.length
        : null;
      const dayIndex = dayIndexForDayNumber(update.day, totalDays);
      if (dayIndex === null) {
        console.warn('[aiChat] dayUpdate aborted — day is not in this trip', update);
        this.reportCardError('day_not_in_trip_range');
        return;
      }

      const success = update.operation === 'remove'
        ? await this.removeDayFromRide({ dayIndex })
        : await this.addNewDayToRide({ dayIndex });

      // Both actions surface their own errors; a false here means they did.
      if (success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
      }
    },

    reportCardError(code) {
      if (this.bulkMode) {
        console.warn('[aiChat] acceptAll — card failed:', code);
        return;
      }
      this.showModalWithParameters({ type: 'textConverter', errorNumber: code });
    },

    // --- Changing the dates, name or notes of a hotel the user has ---
    async applyAccommodationUpdate(update, messageIndex, huIndex) {
      const key = `hu-${messageIndex}-${huIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatUpdateAccommodation({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- Editing an attraction's name or description ---
    async applyAttractionUpdate(update, messageIndex, auIndex) {
      const key = `ae-${messageIndex}-${auIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatUpdateAttraction({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- Notes on a specific day ---
    dayNoteTitleKey(update) {
      if (update.operation === 'remove') return 'aiChatRemoveNoteTitle';
      if (update.operation === 'update') return 'aiChatUpdateNoteTitle';
      return 'aiChatAddNoteTitle';
    },
    async applyDayNoteUpdate(update, messageIndex, dnIndex) {
      const key = `dn-${messageIndex}-${dnIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatApplyDayNote({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- Notes on the trip as a whole ("General Information") ---
    //
    // A day holds at most one note, so its card never has to say WHICH note it
    // means. A trip holds many, so "update" and "remove" carry a note_id and
    // the subtitle spells out that this one belongs to the whole trip -- the
    // single thing a manager could misread as a day note.
    rideNoteTitleKey(update) {
      if (update.operation === 'remove') return 'aiChatRemoveRideNoteTitle';
      if (update.operation === 'update') return 'aiChatUpdateRideNoteTitle';
      return 'aiChatAddRideNoteTitle';
    },
    async applyRideNoteUpdate(update, messageIndex, rnIndex) {
      const key = `rn-${messageIndex}-${rnIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatApplyRideNote({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- The trip description travellers read at the top of the ride ---
    //
    // A description can run to several paragraphs, and the panel is a narrow
    // column beside the itinerary. The card therefore shows a preview -- the
    // first RIDE_TEXT_PREVIEW characters -- with a toggle that opens the whole
    // proposed text in place. Not a diff: the common requests are "rewrite it"
    // and "translate it", where a word-level diff of two paragraphs of Hebrew
    // is harder to read than the new text itself, and the manager approving a
    // replacement needs to read what replaces it, in full, before pressing.
    // Which of the three shapes this card is. A card carrying BOTH fields
    // replaces and then appends, and saying only "Replaces" understates it.
    rideTextSubtitleKey(update) {
      if (update.new_text && update.append_text) return 'aiChatRideTextReplacesAndAdds';
      return update.new_text ? 'aiChatRideTextReplaces' : 'aiChatRideTextAdds';
    },
    // A REPLACEMENT is never previewed -- it is shown in full from the start.
    //
    // The model is handed a capped copy of the description, so a rewrite it
    // proposes can be shorter than what it overwrites; the write has no undo.
    // Approving that from a 220-character preview is the one mistake this
    // card exists to prevent, so for new_text the preview is off by default
    // and the manager reads what lands. An append-only card hides nothing of
    // the manager's own writing, so it keeps the preview.
    rideTextStartsExpanded(update) {
      return !!(update && update.new_text);
    },
    rideTextIsExpanded(update, key) {
      const toggled = this.expandedRideText[key];
      return toggled === undefined ? this.rideTextStartsExpanded(update) : toggled;
    },
    // Both halves of the card are previewed, never hidden: a card carrying
    // new_text AND append_text applies both server-side, so showing only one
    // would have the manager approve text they were not shown.
    rideTextIsLong(update) {
      return [update && update.new_text, update && update.append_text]
        .some(value => (value || '').trim().length > RIDE_TEXT_PREVIEW);
    },
    rideTextShown(value, key, update) {
      const full = (value || '').trim();
      if (this.rideTextIsExpanded(update, key) || full.length <= RIDE_TEXT_PREVIEW) return full;
      return `${full.slice(0, RIDE_TEXT_PREVIEW).trimEnd()}\u2026`;
    },
    toggleRideText(update, key) {
      this.$set(this.expandedRideText, key, !this.rideTextIsExpanded(update, key));
    },
    async applyRideTextUpdate(update, messageIndex, rtIndex) {
      const key = `rt-${messageIndex}-${rtIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatUpdateRideText({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- The trip's name ---
    //
    // Short enough to show whole, so there is no preview and no toggle here:
    // the card is the old name, the new one, and one button. What it shows is
    // the NORMALIZED title -- the same collapse the payload builder applies --
    // so the manager reads the exact string that will be saved rather than a
    // version of it with the model's line breaks still in.
    rideTitleShown(update) {
      return normalizeRideTitle(update).title;
    },
    // A card that cannot be saved is shown with its button disabled and the
    // reason underneath, rather than failing in a popup after the press.
    rideTitleProblem(update) {
      return normalizeRideTitle(update).refusal;
    },
    rideTitleProblemKey(update) {
      return this.rideTitleProblem(update) === 'title_too_long'
        ? 'aiChatRideTitleTooLong' : 'aiChatRideTitleEmpty';
    },
    async applyRideTitleUpdate(update, messageIndex, ttIndex) {
      const key = `tt-${messageIndex}-${ttIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatUpdateRideTitle({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- The trip's language ---
    //
    // Shown as the language's own name, never the stored code, and with a
    // warning when the save will flip the page between Hebrew and English:
    // the ride page then reloads and this conversation, which lives only in
    // memory, closes.
    rideLanguageShown(update) {
      return normalizeRideLanguage(update).language;
    },
    rideLanguageName(code) {
      return rideLanguageLabel(this.t, code);
    },
    rideLanguageProblem(update) {
      return normalizeRideLanguage(update).refusal;
    },
    rideLanguageReloads(update) {
      return rideLanguageSwitchesInterface(this.getCurrentLanguage, this.rideLanguageShown(update));
    },
    async applyRideLanguageUpdate(update, messageIndex, rlIndex) {
      const key = `rl-${messageIndex}-${rlIndex}`;
      if (this.addedSet[key]) return;
      const result = await this.aiChatUpdateRideLanguage({ update });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }
      this.reportCardError(result.code);
    },

    // --- Shifting the trip's start date ---
    async applyRideDateUpdate(update, messageIndex, rdIndex) {
      const key = `rd-${messageIndex}-${rdIndex}`;
      if (this.addedSet[key]) return;
      await this.sendRideDateUpdate(update.new_start_date, key, false);
    },
    async sendRideDateUpdate(newStartDate, key, force) {
      const result = await this.aiChatUpdateRideStartDate({ newStartDate, force });
      if (result && result.success) {
        this.$set(this.addedSet, key, true);
        await this.afterCardApplied(key);
        return;
      }

      // An active booked accommodation blocks the shift until the user accepts
      // that its dates move too -- the same second step the edit screen shows.
      const code = result && result.code;
      if (code === 'rideHaveOrderedAccommodation' && !force) {
        this.showModalWithParameters({
          type: 'confirmForceAiRideDateUpdate',
          inerModalParameters: {
            newStartDate,
            onConfirm: () => this.sendRideDateUpdate(newStartDate, key, true),
          },
        });
        return;
      }

      this.reportCardError(code || 'fail_to_update_attraction');
    },

    // --- Removing an accommodation the AI proposed dropping ---
    isRealBooking(removal) {
      return resolveAccommodationKind(
        this.allRide, removal.accommodation_id, removal.accommodation_type,
      ) === 'booked';
    },
    // A suggestion goes straight away; a real booking is confirmed first,
    // because deleting it also destroys its confirmation and vouchers.
    removeAccommodation(removal, messageIndex, arIndex) {
      const key = `ar-${messageIndex}-${arIndex}`;
      if (this.addedSet[key]) return;

      if (!this.isRealBooking(removal)) {
        this.removeAccommodationConfirmed(removal, key);
        return;
      }

      this.showModalWithParameters({
        type: 'confirmRemoveAiAccommodation',
        inerModalParameters: {
          name: removal.accommodation_name,
          onConfirm: () => this.removeAccommodationConfirmed(removal, key),
        },
      });
    },
    async removeAccommodationConfirmed(removal, key) {
      const result = await this.aiChatRemoveAccommodation({
        accommodationId: removal.accommodation_id,
        accommodationType: removal.accommodation_type,
      });
      if (!result || !result.success) {
        this.reportCardError((result && result.code) || 'fail_to_update_accommodation');
        return;
      }
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },

    // --- "Accept all changes" over every card of one reply ---
    //
    // planAcceptAll (utils/aiChatApply.js) decides what runs and in what order;
    // each card is then applied through its own button's handler, so a bulk
    // run behaves exactly like pressing the buttons one by one.
    acceptAllPlan(msg, messageIndex) {
      return planAcceptAll(msg, {
        isDone: (prefix, i) => !!this.addedSet[`${prefix}-${messageIndex}-${i}`],
        hasPlace: (prefix, i) => !!this.selectedPlaces[`${prefix}-${messageIndex}-${i}`],
      });
    },
    // Cards still looking up their Google place would be skipped as "needs a
    // place", so the button waits for them.
    resolvingAnyCardPlace(messageIndex) {
      return Object.keys(this.resolvingPlaces)
        .some(key => this.resolvingPlaces[key] && key.split('-')[1] === String(messageIndex));
    },
    // --- Auto-continue: running the model after a batch of cards is applied ---
    //
    // Applying cards produces no model turn of its own, so without this the
    // assistant falls silent at every gate, the plan never continues to the
    // next area, and the closing trip check has no trigger at all. When the
    // manager has finished with a reply's cards the panel takes the turn for
    // them. The store's aiChatAutoContinue holds the loop guard; this method
    // holds the rest of it, and nothing here may be moved into a watcher --
    // a reply arriving must never, by itself, produce another turn.
    //
    // Every card handler ends here, so any card can be the one that finishes
    // a batch: the closing trip check mixes hotels with reschedules and the
    // manager may click them in any order. The message index is read back out
    // of the card key ("hu-4-1") because the handlers reached through a
    // confirmation modal are given only the key.
    afterCardApplied(key) {
      return this.maybeAutoContinue(Number(String(key).split('-')[1]));
    },
    // Which of the three jobs this reply's cards serve, or null for a reply
    // that must not run the model.
    //
    // Only the build lane has gates. In the edit lane -- "add a museum on day
    // 3", the everyday case -- the one card the manager applies IS the whole
    // job they asked for, and continuing there would bill a model call per
    // applied batch for ever. msg.lane and msg.progress come from the reply
    // itself (store), so they describe the ride as it was when these cards
    // were emitted: a gate still open there is a gate these cards cross.
    // A reply with no lane -- older, or a backend that does not send one --
    // falls through to null and never continues.
    autoContinueReason(msg) {
      // An edit-lane reply and a reply from a backend that sends no lane at
      // all both return null here, and in the network tab -- which is how
      // this project is accepted -- they look identical: no call. The first
      // is frequent and correct, the second kills the feature outright, so
      // say which one happened.
      if (!msg.lane) console.warn('[aiChat] autoContinue — reply carries no lane; backend not deployed?');
      if (msg.lane !== 'build') return null;
      const progress = msg.progress || {};
      if (msg.areas && msg.areas.length) return progress.areas ? null : 'areas_applied';
      // Both hotels and stops are read against the sleeping gate. Gate 2 runs
      // area by area, so "plan" flips true after the first area and cannot
      // gate the rest of it; once gate 3 is behind the trip, neither the
      // leftover stops nor the gap hotels of the closing trip check may
      // trigger another check.
      //
      // Known and accepted: a ride the manager had already booked hotels for
      // arrives with sleeping true from the start (the backend derives gate 3
      // from the bookings), so gate 2 never auto-continues on it and the
      // manager types to reach each next area. Do not loosen this gate -- the
      // backend shares the limitation (once the first area's stops are in,
      // all three flags are true, build_flag_update clears build_started and
      // the ride leaves the build lane), so nothing the client does here
      // reaches the case. It fails closed, which is the right side to fail on.
      if (msg.hotel_suggestions && msg.hotel_suggestions.length) return progress.sleeping ? null : 'hotels_applied';
      if (msg.suggestions && msg.suggestions.length) return progress.sleeping ? null : 'plan_continue';
      return null;
    },
    // What this reply still has waiting on the manager. Not plan.steps: see
    // outstandingCards in utils/aiChatApply.js for why hotels, which arrive as
    // 2-3 overlapping options per area, need the alternatives folded back in.
    batchOutstanding(msg, messageIndex) {
      return outstandingCards(msg, this.acceptAllPlan(msg, messageIndex), {
        isDone: (prefix, i) => !!this.addedSet[`${prefix}-${messageIndex}-${i}`],
      });
    },
    // "Finished with the batch" is: nothing this reply offered is still
    // waiting. That empties on the last card whether the manager pressed
    // "Accept all" or worked through them one by one. A manager who takes
    // three of five and stops leaves two standing, gets no turn, and
    // continues by typing as before.
    async maybeAutoContinue(messageIndex) {
      const msg = this.aiChatMessages[messageIndex];
      if (!msg || msg.role !== 'assistant') return;
      // One reply can never produce two synthetic turns.
      if (this.autoContinuedFor[messageIndex]) return;
      // Mid-"Accept all": runAcceptAll asks once at the end, so the cards it
      // applies on the way do not each ask.
      if (this.acceptingAll[messageIndex]) return;
      if (this.aiChatLoading) return;
      // A card still looking up its Google place is not "left for you" yet.
      if (this.resolvingAnyCardPlace(messageIndex)) return;
      const reason = this.autoContinueReason(msg);
      if (!reason) return;
      if (this.batchOutstanding(msg, messageIndex).length) return;

      // Set before the await: a card finishing while this turn is being sent
      // must not start a second one. Released again if the store refused --
      // otherwise a manager who closed the panel for a second would have that
      // gate marked as continued with no turn ever sent, and nothing left to
      // send one.
      this.$set(this.autoContinuedFor, messageIndex, true);
      const result = await this.aiChatAutoContinue({ reason });
      if (!result || result.sent !== true) this.$set(this.autoContinuedFor, messageIndex, false);
    },

    // Anything that cannot be undone is listed in one confirmation first.
    acceptAll(msg, messageIndex) {
      if (this.acceptingAll[messageIndex]) return;
      const plan = this.acceptAllPlan(msg, messageIndex);
      if (!plan.steps.length) return;

      if (!plan.removals.length) {
        this.runAcceptAll(plan, messageIndex);
        return;
      }
      this.showModalWithParameters({
        type: 'confirmRemoveAllAiAttractions',
        inerModalParameters: {
          titles: plan.removals.map(step => this.acceptAllRemovalLabel(step)),
          titleKey: 'aiChatAcceptAllConfirmTitle',
          confirmKey: 'aiChatAcceptAll',
          onConfirm: () => this.runAcceptAll(plan, messageIndex),
        },
      });
    },
    acceptAllRemovalLabel({ prefix, card }) {
      if (prefix === 'x') return card.attraction_title;
      if (prefix === 'ar') return `🏨 ${card.accommodation_name}`;
      if (prefix === 'du') return `🗓️ ${this.t('aiChatRemoveDayTitle')} — ${this.t('aiChatDay')} ${card.day}`;
      // A trip note has no day to name it by, so the heading it carries is
      // what tells the manager which of the trip's notes is about to go.
      if (prefix === 'rn') return `📋 ${this.t('aiChatRemoveRideNoteTitle')}${card.title ? ` — ${card.title}` : ''}`;
      return `📝 ${this.t('aiChatRemoveNoteTitle')} — ${this.t('aiChatDay')} ${card.day}`;
    },
    applyCard({ prefix, index, card }, messageIndex) {
      const key = `${prefix}-${messageIndex}-${index}`;
      switch (prefix) {
        case 'x': return this.applyRemoval(card, messageIndex, index);
        // Already confirmed as part of the "Accept all" confirmation.
        case 'ar': return this.removeAccommodationConfirmed(card, key);
        case 'hu': return this.applyAccommodationUpdate(card, messageIndex, index);
        case 'r': return this.applyReschedule(card, messageIndex, index);
        case 'ae': return this.applyAttractionUpdate(card, messageIndex, index);
        case 'u': return this.updateArea(card, messageIndex, index);
        case 'a': return this.approveArea(card, messageIndex, index);
        case 'h': return this.approveHotel(card, messageIndex, index);
        case 's': return this.addSuggestion(card, messageIndex, index);
        case 'dn': return this.applyDayNoteUpdate(card, messageIndex, index);
        case 'rn': return this.applyRideNoteUpdate(card, messageIndex, index);
        case 'rt': return this.applyRideTextUpdate(card, messageIndex, index);
        case 'tt': return this.applyRideTitleUpdate(card, messageIndex, index);
        case 'rl': return this.applyRideLanguageUpdate(card, messageIndex, index);
        case 'du': return this.applyDayUpdate(card, messageIndex, index);
        case 'rd': return this.applyRideDateUpdate(card, messageIndex, index);
        default: return Promise.resolve();
      }
    },
    async runAcceptAll(plan, messageIndex) {
      if (this.acceptingAll[messageIndex]) return;
      this.$set(this.acceptingAll, messageIndex, true);
      this.$set(this.acceptAllNotApplied, messageIndex, 0);
      this.bulkMode = true;

      const isApplied = ({ prefix, index }) => !!this.addedSet[`${prefix}-${messageIndex}-${index}`];
      try {
        for (const step of plan.steps) {
          try {
            await this.applyCard(step, messageIndex);
          } catch (e) {
            console.warn('[aiChat] acceptAll — card threw, continuing', step, e);
          }
          // A day or date change that did not go through -- refused, or waiting
          // on the booked-accommodation popup -- leaves the trip in a state the
          // remaining day cards were not numbered against, so stop there.
          if (!isApplied(step) && (step.prefix === 'du' || step.prefix === 'rd')) break;
        }
      } finally {
        this.bulkMode = false;
        this.$set(this.acceptingAll, messageIndex, false);
      }

      const notApplied = plan.steps.filter(step => !isApplied(step)).length;
      this.$set(this.acceptAllNotApplied, messageIndex, notApplied);
      console.log('[aiChat] acceptAll — steps:', plan.steps.length, 'not applied:', notApplied);

      // The batch is in. One turn for the whole run, never one per card:
      // acceptingAll is already false, so the check runs for real this time.
      await this.maybeAutoContinue(messageIndex);
    },

    async applyRemoval(removal, messageIndex, removalIndex) {
      const key = `x-${messageIndex}-${removalIndex}`;
      if (this.addedSet[key]) return;
      await this.aiChatRemoveAttraction({ attractionId: removal.attraction_id });
      this.$set(this.addedSet, key, true);
      await this.afterCardApplied(key);
    },
    extractCityCountry(place) {
      if (!place.address_components) return place.formatted_address || '';
      const get = (type) => {
        const c = place.address_components.find(a => a.types.includes(type));
        return c ? c.long_name : null;
      };
      const city = get('locality') || get('administrative_area_level_2') || get('administrative_area_level_1');
      const country = get('country');
      return [city, country].filter(Boolean).join(', ');
    },
    // Try the English Google Maps query the AI supplies first, then the
    // display name (usually Hebrew). When neither is found, leave the English
    // query in the search box so the user only has to open the suggestions.
    async autoResolvePlace(key, placeQuery, displayName) {
      if (this.selectedPlaces[key]) return;
      // Flagged while in flight so "Add all" can wait for it: a card still
      // resolving is in neither selectedPlaces nor needsManualPlace, and would
      // otherwise be skipped silently.
      this.$set(this.resolvingPlaces, key, true);
      try {
        const queries = [...new Set([placeQuery, displayName].filter(Boolean))];
        for (const query of queries) {
          const place = await this.resolvePlace(query);
          if (place) {
            this.$set(this.selectedPlaces, key, place);
            return;
          }
        }
        this.$set(this.needsManualPlace, key, true);
        if (queries.length) this.preseedPlaceInput(key, queries[0]);
      } finally {
        this.$set(this.resolvingPlaces, key, false);
      }
    },
    preseedPlaceInput(key, query) {
      this.$nextTick(() => {
        const ref = this.$refs['placeInput-' + key];
        const component = Array.isArray(ref) ? ref[0] : ref;
        const el = component && component.$el;
        const input = el && (el.tagName === 'INPUT' ? el : el.querySelector('input'));
        if (!input || input.value) return;
        input.value = query;
      });
    },
    resolvePlace(query) {
      // resolvePlace now reports the Places status alongside the place; this
      // panel only cares whether it got one.
      return resolvePlace(this.$gmapApiPromiseLazy.bind(this), query)
        .then(({ place }) => place);
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.ai-chat-trigger-container {
  max-width: calc(100vw - 40px);
}

.ai-chat-badge {
  display: inline-block;
  margin-inline-start: 6px;
  background: #fff;
  color: #2979ff;
  border-radius: 10px;
  min-width: 18px;
  padding: 0 5px;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}

.ai-chat-open-btn {
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 12px 20px;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(41, 121, 255, 0.4);
  white-space: nowrap;
}


.ai-panel {
  position: fixed;
  top: 56px;
  right: 0;
  width: 380px;
  height: calc(100vh - 56px);
  background: #fff;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 1100;
}
.ai-panel--rtl {
  right: auto;
  left: 0;
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.15);
}

.ai-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f8f8;
  flex-shrink: 0;
}
.ai-panel__title { font-weight: 600; font-size: 16px; }
.ai-panel__close {
  background: #e0e0e0;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 16px;
  cursor: pointer;
  color: #333;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ai-panel__close:hover {
  background: #ccc;
}

.ai-panel__profile-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  font-size: 12px;
  color: #444;
  background: #eef4ff;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
}
.ai-panel__profile-summary {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* The placeholder is a button, so it needs the strip's own look back and must
   follow the text direction rather than centring like a default button. */
.ai-panel__profile-summary--empty {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #2979ff;
  text-align: inherit;
  cursor: pointer;
}
.ai-panel__profile-edit {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 15px;
  color: #2979ff;
}
.ai-panel__profile-change-row {
  display: flex;
  gap: 6px;
  align-items: baseline;
  margin-top: 3px;
  font-size: 12px;
}
.ai-panel__profile-change-tag {
  flex-shrink: 0;
  color: #888;
  font-size: 11px;
}
.ai-panel__profile-change-value {
  min-width: 0;
  overflow-wrap: anywhere;
  color: #333;
}
.ai-panel__profile-change-error {
  display: block;
  margin-top: 4px;
  color: #c62828;
  font-size: 12px;
}
.ai-panel__profile-change-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}
.ai-panel__profile-keep-btn {
  background: #fff;
  color: #555;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.ai-panel__profile-keep-btn:disabled {
  color: #aaa;
  cursor: default;
}
.ai-panel__suggestion-card--profile {
  border-color: #f9a825;
}
.ai-panel__profile-drawer {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.ai-panel__profile-error {
  margin: 12px 16px 0;
  color: #c62828;
  font-size: 13px;
}
.ai-panel__profile-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f8f8;
  flex-shrink: 0;
}
.ai-panel__context-bar {
  padding: 8px 16px;
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
}

.ai-panel__messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-panel__message {
  max-width: 90%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
}
.ai-panel__message--user {
  align-self: flex-end;
  background: #2979ff;
  color: #fff;
  border-bottom-right-radius: 2px;
}
.ai-panel__message--assistant {
  align-self: flex-start;
  background: #f0f0f0;
  color: #222;
  border-bottom-left-radius: 2px;
}
.ai-panel__message-text {
  margin: 0 0 4px;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-panel__suggestions {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ai-panel__suggestion-card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}
.ai-panel__suggestion-info { flex: 1; }
.ai-panel__suggestion-day {
  display: block;
  font-size: 11px;
  color: #888;
  margin: 2px 0;
}
.ai-panel__suggestion-desc {
  margin: 4px 0 0;
  font-size: 12px;
  color: #555;
}
/* The proposed description keeps the manager's own line breaks, and wraps
   long words rather than widening the card. No physical direction is set:
   the text inherits the panel's, so Hebrew renders RTL and English LTR. */
.ai-panel__ride-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
/* Stakes line on a replacement card: it overwrites the manager's own text
   and there is no undo. Direction-neutral -- margin left/right are both 0. */
.ai-panel__ride-text-warning {
  margin: 4px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: #e65100;
}
.ai-panel__ride-text-toggle {
  display: inline-block;
  margin: 4px 0 0;
  padding: 0;
  background: none;
  border: none;
  color: #2979ff;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.ai-panel__add-btn {
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.ai-panel__add-btn:disabled {
  background: #aaa;
  cursor: default;
}

.ai-panel__suggestion-card--area {
  border-color: #43a047;
}
.ai-panel__area-card-inner {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}
.ai-panel__area-autocomplete {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 13px;
  font-family: inherit;
  box-sizing: border-box;
}
.ai-panel__area-autocomplete:focus {
  outline: none;
  border-color: #43a047;
}
.ai-panel__area-autocomplete--manual {
  border-color: #fb8c00;
  background: #fff8f0;
}
.ai-panel__area-autocomplete--manual:focus {
  border-color: #fb8c00;
}
.ai-panel__manual-hint {
  font-size: 11px;
  color: #fb8c00;
  font-weight: 600;
}
.ai-panel__approve-btn {
  background: #43a047;
  align-self: flex-end;
}
.ai-panel__approve-btn:disabled {
  background: #aaa;
  cursor: default;
}
.ai-panel__area-approved {
  color: #43a047;
  font-size: 13px;
  font-weight: 600;
}
.ai-panel__resolved-place {
  font-size: 12px;
  color: #43a047;
  font-weight: 600;
}
.ai-panel__suggestion-card--hotel {
  border-color: #00897b;
}
.ai-panel__hotel-btn {
  background: #00897b;
  align-self: flex-end;
}
.ai-panel__hotel-btn:disabled {
  background: #aaa;
  cursor: default;
}
.ai-panel__hotel-added {
  color: #00897b;
  font-size: 13px;
  font-weight: 600;
}

.ai-panel__suggestion-card--area-update {
  border-color: #fb8c00;
}
.ai-panel__update-btn {
  background: #fb8c00;
  align-self: flex-end;
}
.ai-panel__update-btn:disabled {
  background: #aaa;
  cursor: default;
}
.ai-panel__area-updated {
  color: #fb8c00;
  font-size: 13px;
  font-weight: 600;
}

.ai-panel__apply-all {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid #f59300;
  border-radius: 8px;
  background: #fff6e8;
}
.ai-panel__accept-all {
  border-color: #28a745;
  background: #eaf7ee;
  /* Pinned to the top of the panel while this reply's cards scroll under it.
     The bar only renders while the reply still has more than one unapplied
     card, so a reply the manager has dealt with stops pinning anything. */
  position: sticky;
  top: 0;
  z-index: 2;
}
.ai-panel__accept-all-btn {
  background: #28a745;
}
/* Cards of a reply "Accept all" is working through cannot be pressed meanwhile. */
.ai-panel__message--busy .ai-panel__suggestions .ai-panel__add-btn {
  pointer-events: none;
  opacity: 0.6;
}
.ai-panel__apply-all-count {
  font-size: 13px;
  font-weight: 600;
  color: #b06a00;
}

.ai-panel__suggestion-card--reschedule {
  border-color: #f59300;
}
.ai-panel__move-btn {
  background: #f59300;
}
.ai-panel__move-btn:disabled {
  background: #aaa;
}

.ai-panel__suggestion-card--ride-date {
  border-color: #3f51b5;
}
.ai-panel__ride-date-btn {
  background: #3f51b5;
}
.ai-panel__ride-date-btn:disabled {
  background: #aaa;
}

.ai-panel__suggestion-card--removal {
  border-color: #dc3545;
}
.ai-panel__remove-btn {
  background: #dc3545;
}
.ai-panel__remove-btn:disabled {
  background: #aaa;
}

.ai-panel__starters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 8px;
}
.ai-panel__starter-btn {
  background: #f0f4ff;
  color: #2979ff;
  border: 1.5px solid #c5d5ff;
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 14px;
  text-align: start;
  cursor: pointer;
  transition: background 0.15s;
}
.ai-panel__starter-btn:hover {
  background: #dce8ff;
}

.ai-panel__quick-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.ai-panel__quick-reply-btn {
  background: #fff;
  color: #2979ff;
  border: 1.5px solid #2979ff;
  border-radius: 16px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.ai-panel__quick-reply-btn:hover {
  background: #2979ff;
  color: #fff;
}

.ai-panel__typing {
  align-self: flex-start;
  display: flex;
  gap: 4px;
  padding: 10px 14px;
}
.ai-panel__typing-dot {
  width: 8px;
  height: 8px;
  background: #aaa;
  border-radius: 50%;
  animation: ai-blink 1.2s infinite;
}
.ai-panel__typing-dot:nth-child(2) { animation-delay: 0.2s; }
.ai-panel__typing-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes ai-blink {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}

.ai-panel__input-area {
  padding: 12px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}
.ai-panel__textarea {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 8px;
  font-size: 14px;
  resize: none;
  box-sizing: border-box;
  font-family: inherit;
}
.ai-panel__send-btn {
  align-self: flex-end;
  background: #2979ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: 14px;
  cursor: pointer;
}
.ai-panel__send-btn:disabled {
  background: #aaa;
  cursor: default;
}

.ai-panel__composer-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.ai-panel__attach-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid #ccc;
  border-radius: 50%;
  background: #fff;
  color: #2979ff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ai-panel__attach-btn:hover {
  background: #f0f4ff;
}
.ai-panel__file-notice {
  margin: 0;
  font-size: 13px;
  color: #b45309;
}

.ai-panel__drop-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 18px 14px;
  border: 2px dashed #9bb8ff;
  border-radius: 12px;
  background: #f5f8ff;
  color: #222;
  font-size: 13px;
  text-align: center;
  cursor: pointer;
}
.ai-panel__drop-tile strong {
  font-size: 15px;
}
.ai-panel__drop-tile-types {
  color: #666;
  font-size: 12px;
}

.ai-panel__drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(41, 121, 255, 0.12);
  border: 3px dashed #2979ff;
  font-size: 16px;
  font-weight: 600;
  color: #1a56c4;
  text-align: center;
  pointer-events: none;
}

.ai-panel__doc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ai-panel__doc-chip {
  padding: 4px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 13px;
  word-break: break-all;
}

.ai-panel-slide-enter-active,
.ai-panel-slide-leave-active {
  transition: transform 0.3s ease;
}
.ai-panel-slide-enter,
.ai-panel-slide-leave-to {
  transform: translateX(100%);
}
.ai-panel--rtl.ai-panel-slide-enter,
.ai-panel--rtl.ai-panel-slide-leave-to {
  transform: translateX(-100%);
}

@media (max-width: 480px) {
  .ai-panel {
    width: 100vw;
    top: 0;
    height: 100vh;
    height: 100dvh;
  }
  .ai-panel__header {
    padding-top: calc(16px + env(safe-area-inset-top, 0px));
  }
.ai-panel__messages {
    padding: 8px;
  }
  .ai-panel__message {
    max-width: 95%;
    font-size: 15px;
  }
  .ai-panel__add-btn {
    padding: 10px 14px;
    font-size: 13px;
  }
  .ai-panel__textarea {
    font-size: 16px; /* prevents iOS zoom on focus */
    rows: 2;
  }
  .ai-panel__send-btn {
    width: 100%;
    padding: 12px;
    font-size: 15px;
  }
  .ai-panel__suggestion-card {
    flex-direction: column;
  }
  .ai-panel__add-btn {
    align-self: stretch;
    text-align: center;
  }
  .ai-panel__quick-reply-btn {
    padding: 10px 16px;
    font-size: 14px;
  }
  .ai-chat-open-btn {
    padding: 14px 22px;
    font-size: 16px;
  }
}
</style>

<style>
/* Google appends the autocomplete dropdown to <body> with z-index 1000,
   which puts it behind the fixed .ai-panel (z-index 1100). */
.pac-container {
  z-index: 1200 !important;
}

/* bootstrap-vue wraps every modal in an outer div with an inline z-index of
   1040, below the panel's 1100. On a phone the panel is full screen, so the
   confirmations the chat opens (moving the trip's dates, removing a stay)
   were hidden behind it. Lifted only while the panel is open. */
body.ai-panel-open [id$="___BV_modal_outer_"] {
  z-index: 1150 !important;
}
</style>
